import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Flex,
    HStack,
    IconButton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import ErrorState from '../../../components/states/ErrorState';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { ProductDto } from '../../../models/product/dto/product.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useDeleteProduct } from '../hooks/useDeleteProduct';
import { useListProducts } from '../hooks/useListProducts';
import { formatProductPrice, getCategoryColor } from '../utils/product.utils';
import CreateProductForm from './CreateProductForm';
import EditProductModal from './EditProductModal';

const columnHelper = createColumnHelper<ProductDto>();

export default function ProductList() {
    const { selectedLocation } = useSelectedLocation();
    const { data, isLoading, isFetching, isError } = useListProducts(selectedLocation?.id);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [editingProduct, setEditingProduct] = useState<ProductDto | null>(null);
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const toast = useToast();

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const deleteProductMutation = useDeleteProduct();

    const handleEdit = (product: ProductDto) => {
        setEditingProduct(product);
        onEditOpen();
    };

    const handleDelete = async (productId: string) => {
        if (window.confirm(translate('product.delete_confirm') || 'Are you sure you want to delete this product?')) {
            deleteProductMutation.mutate(productId, {
                onSuccess: () => {
                    toast({
                        title: translate('product.delete_success') || 'Product deleted successfully',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                },
                onError: (error) => {
                    toast({
                        title: translate('product.delete_error') || 'Error deleting product',
                        description: error instanceof Error ? error.message : (translate('product.unknown_error') || 'Unknown error'),
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.name') || 'Name'}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('category', {
            id: 'category',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.category') || 'Category'}
                </Text>
            ),
            cell: (info: any) => (
                <Badge colorScheme={getCategoryColor(info.getValue())} variant="subtle">
                    {translate(`product.categories.${info.getValue()}` as any) || info.getValue()}
                </Badge>
            )
        }),
        columnHelper.accessor('price', {
            id: 'price',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.price') || 'Price'}
                </Text>
            ),
            cell: (info: any) => {
                const row = info.row.original;
                return (
                    <Text color={textColor} fontSize='sm' fontWeight='600'>
                        {formatProductPrice(info.getValue(), row.currency)}
                    </Text>
                );
            }
        }),
        columnHelper.accessor('stockQuantity', {
            id: 'stockQuantity',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.stock') || 'Stock'}
                </Text>
            ),
            cell: (info: any) => {
                const stock = info.getValue();
                const isUnlimited = info.row.original.hasUnlimitedStock;
                return (
                    <Badge
                        colorScheme={isUnlimited ? 'green' : stock > 0 ? 'brand' : 'red'}
                        variant="subtle"
                    >
                        {isUnlimited ? (translate('product.stock_unlimited') || 'Unlimited') : stock === 0 ? (translate('product.stock_out_of_stock') || 'Out of Stock') : stock}
                    </Badge>
                );
            }
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.status') || 'Status'}
                </Text>
            ),
            cell: (info: any) => (
                <Badge
                    colorScheme={info.getValue() ? 'green' : 'red'}
                    variant="subtle"
                >
                    {info.getValue() ? (translate('product.status_active') || 'Active') : (translate('product.status_inactive') || 'Inactive')}
                </Badge>
            )
        }),
        columnHelper.accessor('sports', {
            id: 'sports',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.sports') || 'Sports'}
                </Text>
            ),
            cell: (info: any) => {
                const sports = info.getValue() || [];
                return (
                    <HStack spacing={1} wrap="wrap">
                        {sports.slice(0, 2).map((sport: string) => (
                            <Badge key={sport} size="sm" variant="outline">
                                {sport}
                            </Badge>
                        ))}
                        {sports.length > 2 && (
                            <Badge size="sm" variant="outline">
                                +{sports.length - 2}
                            </Badge>
                        )}
                    </HStack>
                );
            }
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('product.actions') || 'Actions'}
                </Text>
            ),
            cell: (info: any) => {
                const product = info.row.original;
                return (
                    <HStack spacing={2}>
                        <IconButton
                            aria-label="Edit product"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="brand"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(product);
                            }}
                        />
                        <IconButton
                            aria-label="Delete product"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(product.id);
                            }}
                        />
                    </HStack>
                );
            }
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <PageCard title={translate('tabs.products') || 'Products'} rightHeader={<CreateProductForm />}>
                <Box>
                    <Table {...tableProps}>
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <Th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                {...tableThProps}
                                                borderColor={borderColor}
                                                cursor='pointer'
                                                onClick={header.column.getToggleSortingHandler()}>
                                                <Flex
                                                    {...tableThFlexProps}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    <SortIndicator direction={header.column.getIsSorted()} />
                                                </Flex>
                                            </Th>
                                        );
                                    })}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <Tr
                                        {...tableRowProps}
                                        key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <Td
                                                    key={cell.id}
                                                    {...tableCellProps}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                    <Loader isLoading={(isLoading || isFetching) && ((data || []).length === 0)} />
                    {isError && (
                        <ErrorState message={translate('product.error_loading') || 'Failed to load products'} />
                    )}
                    {!isLoading && !isFetching && !isError && (data || []).length === 0 && (
                        <Text textAlign="center" py={8} color={textColor}>
                            {translate('product.no_products') || 'No products found'}
                        </Text>
                    )}
                </Box>
            </PageCard>

            {editingProduct && (
                <EditProductModal
                    isOpen={isEditOpen}
                    onClose={() => {
                        onEditClose();
                        setEditingProduct(null);
                    }}
                    product={editingProduct}
                />
            )}
        </>
    );
}
